import { debug } from "./debug";

/**
 * Retry a function multiple times with exponential backoff
 * @param fn The function to retry
 * @param attempts The number of attempts to make
 * @param delay The initial delay in milliseconds
 * @returns A promise that resolves with the result of the function
 */
export async function retry<T>(
  fn: () => Promise<T>,
  attempts: number,
  delay = 1000,
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (attempts <= 1) {
      throw error;
    }

    // Calculate backoff with jitter
    const jitter = Math.random() * 0.3 + 0.85; // 0.85-1.15
    const backoff = Math.min(delay * jitter, 30000); // Cap at 30 seconds

    debug(`Retrying after ${backoff}ms, ${attempts - 1} attempts left`);

    // Wait for the backoff period
    await new Promise((resolve) => setTimeout(resolve, backoff));

    // Retry with one less attempt and increased delay
    return retry(fn, attempts - 1, delay * 2);
  }
}
